import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';

import {
    Typography, Table, TableHead, TableRow, TableCell, TableBody, ButtonBase, TextField,
} from '@material-ui/core';

import HtmlContent from '../../../HtmlContent';
import { CheckedIcon } from '../../../../../common/icons';
import { isEmpty } from '../../../../objectUtils';
import { isEdge } from '../../../../browserUtils';
import { with2dArrowNavigation } from '../../../accessibility/ArrowNavigation';
import { formAnswerTypes } from '../../../../constants';
import { getInputType } from '../FreetextResponse';
import { parseBoolString } from '../../../../stringUtils';
import { getStylesFromSettings } from '../../utils';

const cellWidth = 40;
const cellWidthSmall = 30;
const maxContentWidth = 1080;

const styles = theme => ({
    table: {
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.grey[100]}`,
        background: theme.palette.common.white,

        [theme.breakpoints.down('xs')]: {
            tableLayout: 'fixed',
        },
    },
    stickyHeaderCell: {
        position: 'sticky',
        top: 49,
        background: theme.palette.common.white,
        boxShadow: `0 2px 2px -2px ${theme.palette.grey[600]}`,
        zIndex: 50,
    },
    headerCell: {
        border: `1px solid ${theme.palette.grey[200]}`,
        borderBottom: '1px solid #e0e0e0',
        padding: theme.spacing(1),
        textAlign: 'center',

        '$rotate &': {
            verticalAlign: 'bottom',
            width: cellWidth,
            minHeight: 140, // iOs/Safari fix - height auto is not working.. Should have a better fix, or be removed when iOs is updated!

            [theme.breakpoints.down('xs')]: {
                padding: theme.spacing(1, 0),
                width: cellWidthSmall,
            },
        },

    },
    answerTextCell: {
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1, 0.5),
        },
        overflowWrap: 'anywhere',
    },
    answerCell: {
        border: `1px solid ${theme.palette.grey[200]}`,
        borderBottom: '1px solid #e0e0e0',
        padding: theme.spacing(0.5),
        textAlign: 'center',
        height: '100%',

        '$rotate &': {
            width: cellWidth,

            [theme.breakpoints.down('xs')]: {
                padding: 1,
                width: cellWidthSmall,
            },
        },
    },
    scoreCell: {
        background: theme.palette.grey[300],
    },
    invalidRow: {
        borderLeft: `2px solid ${theme.palette.error.main}`,
    },
    answerOption: {

        '$rotate &': {
            display: 'inline-block',
            writingMode: 'vertical-lr',
            transform: 'rotate(180deg)',
            whiteSpace: 'nowrap',
            maxHeight: 300,
            overflow: 'hidden',
        },
    },
    rotate: {

    },
    answerButton: {
        // width: cellWidth,
        // height: cellWidth,
        width: '100%',
        height: '100%',

        '&:focus': {
            background: theme.palette.grey[200],
        },

        '&:hover': {
            background: theme.palette.grey[300],
        },

        // [theme.breakpoints.down('xs')]: {
        //     width: cellWidthSmall - 2,
        //     height: cellWidthSmall - 2,
        // },
    },
    cellMarker: {
        opacity: 0,

        [theme.breakpoints.down('xs')]: {
            opacity: 0.2,
        },
    },
});

const colStyle = (elementSettings, colIndex) => getStylesFromSettings(elementSettings, `col${colIndex + 1}style.`)

class QuestionnaireTable extends React.Component {
    selectAnswer = (question, answer) => () => {
        const selectedChoices = this.props.responses?.[question.id]?.response?.choiceResponse || [];
        const wasCheckedBefore = selectedChoices.findIndex(d => d.id === answer.id) > -1;

        if (question.type === formAnswerTypes.multipleChoice) {
            const values = wasCheckedBefore ? selectedChoices.filter(d => d.id !== answer.id) : [...selectedChoices, answer];
            this.props.onChange(question)('choiceResponse', values);
        } else {
            const values = wasCheckedBefore ? null : [answer];
            this.props.onChange(question)('choiceResponse', values);
        }
    }

    handleFreetextChange = question => (event) => {
        this.props.onChange(question)('freetextResponse', event.target.value);
    }

    render() {
        const {
            classes, questions, responses, disabled, showScoreMarks, validationError, navigation, elementSettings, readOnly,
        } = this.props;

        if (!questions || questions.length < 1 || !questions[0].answers) {
            return null;
        }

        const respondedAnswers = questions.reduce((prev, curr) => ({
            ...prev,
            [curr.id]: responses?.[curr.id]?.response.choiceResponse?.length > 0
                ? responses[curr.id].response.choiceResponse.map(d => d.id)
                : [responses?.[curr.id]?.response?.freetextResponse],
        }), {});


        const verticalText = parseBoolString(elementSettings?.flipLabels) || (document.documentElement.clientWidth < 930 && !parseBoolString(elementSettings?.dontFlipLabels));
        const firstColWidth = verticalText ? document.documentElement.clientWidth - (questions[0].answers.length * cellWidthSmall) - 16 - 16
            : Number(elementSettings?.firstColWidth) || '';
        // Writing mode fix for Edge - until https://github.com/cssinjs/css-vendor/issues/172 is merged into Material UI/Jss
        const ieFix = verticalText && isEdge ? { 'writing-mode': 'vertical-lr' } : null;
        const answerCellMinWidthDefault = Number(elementSettings?.answerCellMinWidth) || (firstColWidth ? 40 : 100);
        const answerCellMinWidth = verticalText
            ? null
            : Math.min(Math.min(document.documentElement.clientWidth, maxContentWidth) / (questions[0].answers.length * 2), answerCellMinWidthDefault);

        return (
            <Table className={classnames(classes.table, { [classes.rotate]: verticalText })}>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.stickyHeaderCell} style={{ width: firstColWidth }} />
                        {questions[0].answers.map(item => (
                            <TableCell key={item.id} className={classnames(classes.headerCell, classes.stickyHeaderCell)} style={{ minWidth: answerCellMinWidth }}>
                                <Typography variant="body1" component="span" className={classes.answerOption} style={ieFix}>
                                    {item.value}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {questions.map((question, ri) => (
                        <TableRow key={question.id} className={validationError && question.required && isEmpty(respondedAnswers[question.id]) ? classes.invalidRow : null}>
                            <TableCell className={classes.answerTextCell}>
                                <HtmlContent html={question.question} className={classes.questionText} />
                            </TableCell>
                            {question.type === formAnswerTypes.singleChoice || question.type === formAnswerTypes.multipleChoice ? (
                                <>
                                    {question.answers?.map((item, ci) => (
                                        <TableCell key={item.id} className={classnames(classes.answerCell, { [classes.scoreCell]: showScoreMarks && item.score > 0 })} style={colStyle(elementSettings, ci)}>
                                            <ButtonBase
                                                disabled={disabled || readOnly}
                                                onClick={this.selectAnswer(question, item)}
                                                className={classes.answerButton}
                                                {...{
                                                    ref: navigation.setRef(ri, ci),
                                                    tabIndex: -1,
                                                    onKeyDown: navigation.handleKeyDown,
                                                    onMouseDown: navigation.handleMouseDown(ri, ci),
                                                    ...ri === 0 && ci === 0 && {
                                                        tabIndex: 0,
                                                        onFocus: navigation.handleMouseDown(ri, ci),
                                                    },
                                                }}
                                            >
                                                {respondedAnswers[question.id]?.findIndex(d => d === item.id) > -1 ? (
                                                    <CheckedIcon color="primary" />
                                                ) : (
                                                    <CheckedIcon color="disabled" className={classes.cellMarker} />
                                                )}
                                            </ButtonBase>
                                        </TableCell>
                                    ))}
                                </>
                            ) : (
                                <TableCell colSpan={question.answers?.length} className={classnames(classes.answerCell)} style={colStyle(elementSettings, 0)}>
                                    <TextField
                                        type={getInputType(question.freetextConfig?.inputType)}
                                        fullWidth
                                        disabled={disabled}
                                        value={respondedAnswers[question.id]?.[0] || ''}
                                        onChange={this.handleFreetextChange(question)}
                                        className={classes.inputResponse}
                                        InputProps={{
                                            disableUnderline: true,
                                            inputProps: { maxLength: question.freetextConfig?.maxLength },
                                        }}
                                    // helperText={this.state.validationErrors.freetextResponse ? translate(this.state.validationErrors.freetextResponse) : null}
                                    // error={!!this.state.validationErrors.freetextResponse}
                                    />
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }
}


QuestionnaireTable.propTypes = {
    classes: PropTypes.object.isRequired,
    navigation: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    questions: PropTypes.array.isRequired,
    responses: PropTypes.object,
    disabled: PropTypes.bool,
    showScoreMarks: PropTypes.bool,
    validationError: PropTypes.bool,
    elementSettings: PropTypes.object,
    readOnly: PropTypes.bool,
};

QuestionnaireTable.defaultProps = {
    disabled: false,
    showScoreMarks: false,
    responses: null,
    validationError: false,
    elementSettings: null,
    readOnly: false,
};

export default withStyles(styles)(with2dArrowNavigation(QuestionnaireTable));
